import React, { forwardRef, useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { Input } from "./ui/input";

/**
 * PasswordInput — drop-in replacement for `<Input type="password" />` with:
 *  • An inline eye/eye-off toggle on the right (screen-reader labeled).
 *  • Optional Caps Lock warning that appears while the input has focus.
 *
 * Notes:
 *  • The toggle button is always rendered (works fine on mobile touch too);
 *    Caps Lock detection is gated to desktop-ish inputs (KeyboardEvent
 *    reports getModifierState reliably there; virtual keyboards typically
 *    do not fire it, so we simply won't show a false positive on mobile).
 *  • Any extra props (data-testid, onChange, autoComplete, required, etc.)
 *    are passed straight through to the underlying <Input>.
 */
const PasswordInput = forwardRef(function PasswordInput(
  {
    className = "",
    showCapsLockWarning = true,
    capsWarningTestId,
    onKeyDown,
    onKeyUp,
    onBlur,
    onFocus,
    ...rest
  },
  ref
) {
  const [visible, setVisible] = useState(false);
  const [capsOn, setCapsOn] = useState(false);
  const [focused, setFocused] = useState(false);

  // Detect Caps Lock via KeyboardEvent.getModifierState — the only reliable
  // way across browsers. We check on keydown/keyup so the state stays in
  // sync even when the user toggles Caps Lock without pressing a character.
  const updateCaps = (e) => {
    if (typeof e?.getModifierState === "function") {
      setCapsOn(e.getModifierState("CapsLock"));
    }
  };

  const handleKeyDown = (e) => {
    updateCaps(e);
    if (onKeyDown) onKeyDown(e);
  };
  const handleKeyUp = (e) => {
    updateCaps(e);
    if (onKeyUp) onKeyUp(e);
  };
  const handleFocus = (e) => { setFocused(true); if (onFocus) onFocus(e); };
  const handleBlur = (e) => { setFocused(false); setCapsOn(false); if (onBlur) onBlur(e); };

  return (
    <div className="relative">
      <Input
        ref={ref}
        type={visible ? "text" : "password"}
        className={`${className} pr-11`}
        onKeyDown={handleKeyDown}
        onKeyUp={handleKeyUp}
        onFocus={handleFocus}
        onBlur={handleBlur}
        {...rest}
      />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        onMouseDown={(e) => e.preventDefault()} // keep focus on the input
        className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex h-8 w-8 items-center justify-center rounded-md text-ink-500 hover:bg-ink-100 hover:text-ink-900 focus:outline-none focus:ring-2 focus:ring-brand/30 transition-colors"
        aria-label={visible ? "Hide password" : "Show password"}
        aria-pressed={visible}
        data-testid={rest["data-testid"] ? `${rest["data-testid"]}-toggle` : "password-toggle"}
        tabIndex={-1}
      >
        {visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>

      {/* Caps Lock warning — subtle inline hint, only while focused. */}
      {showCapsLockWarning && focused && capsOn ? (
        <p
          role="status"
          aria-live="polite"
          className="mt-1 text-xs font-medium text-amber-700"
          data-testid={capsWarningTestId || "caps-lock-warning"}
        >
          Caps Lock is on.
        </p>
      ) : null}
    </div>
  );
});

export default PasswordInput;
